import {ChangeDetectionStrategy, Component, Inject} from "@angular/core";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {DokumentFormatIkonKomponent} from "web-ng2/moduler/dokument/komponenter/dokument-format-ikon/dokument-format-ikon";
import {MatIcon} from "@angular/material/icon";
import {MatIconButton} from "@angular/material/button";
import {MatMenuTrigger} from "@angular/material/menu";
import {TranslateModule} from "@ngx-translate/core";
import {DokumentMenyKomponent} from "web-ng2/moduler/dokument/komponenter/dokument-meny/dokument-meny";
import {DokumentvisningKomponentAvhengigheter} from "web-ng2/moduler/dokument/komponenter/dokumentvisning/DokumentvisningKomponentAvhengigheter";
import {DelegerteDokumentvisningOperasjoner, DelegerteDokumentvisningOperasjoner_DI_TOKEN, DokumentvisningBasisKomponent} from "web-ng2/moduler/dokument/komponenter/dokumentvisning/DokumentvisningBasisKomponent";
import {MatTooltip} from "@angular/material/tooltip";
import {DokumentInlinePdfviserKomponent} from "web-ng2/moduler/dokument/komponenter/dokument-inline-pdfviser/dokument-inline-pdfviser";
import {animate, AnimationEvent, query, style, transition, trigger} from "@angular/animations";
import {FormaterPlainDatePipe} from "felles-ng2/pipes/FormaterPlainDatePipe";
import {ZonedDateTimeSomPlainDatePipe} from "felles-ng2/pipes/ZonedDateTimeSomPlainDatePipe";
import {TittelKomponent, TittelVerdiKomponent} from "web-ng2/delt/komponenter/tittel-verdi/tittel-verdi";
import {Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {DokumentInformasjonTjeneste} from "felles-ng2/tjenester/DokumentInformasjonTjeneste";
import {ÅpneDokumentTjeneste} from "web-ng2/moduler/dokument/tjenester/ÅpneDokumentTjeneste";
import {DokumentSigneringTjeneste} from "web-ng2/moduler/dokument/tjenester/DokumentSigneringTjeneste";
import {KlikkMedStopPropagationDirektiv} from "web-ng2/delt/direktiver/klikkMedStopPropagation/klikkMedStopPropagation";
import {Navigasjon} from "web-ng2/tjenester/Navigasjon";

const debug = initDebugger('DokumentListevisningKomponent');

export type ÅpneValg = { inline: boolean };

/**
 * Visning av dokument for bruk i listeorientert visning.
 * Merk at kallende komponent forventes å ha tilgjengeliggjort:
 * - {@link DelegerteDokumentvisningOperasjoner} under tokenet {@link DelegerteDokumentvisningOperasjoner_DI_TOKEN}, som konsumeres av {@link DokumentKortvisningKomponent}
 * - {@link DelegerteDokumentMenyOperasjoner} under tokenet {@link DelegerteDokumentMenyOperasjoner_DI_TOKEN}, som konsumeres av {@link DokumentMenyKomponent}
 */
@Component({
    selector: 'dokument-listevisning',
    templateUrl: 'dokument-listevisning.html',
    styleUrl: 'dokument-listevisning.scss',
    host: {
        //følgende kunne ha ligget i DokumentvisningBasisKomponent men det gir feil av typen `NG0100: ExpressionChangedAfterItHasBeenCheckedError`
        '(dblclick)': 'aapneInline()',
        '[class.viser-inline]': 'viserInline()',
        '[attr.id]': '`dok-${dokumentversjon().dokumentId}`',
        'data-testid': 'dokument',
        //DokumentListevisningKomponent spesifikke host-attributter
        '[class.vis-inline-pdfviser]': 'viserInline()',
        '[style.--dokument-høyde-ekspandert]': 'dokumentHoeydeEkspandert',
    },
    animations: [skalerDokumentVedInlineVisning()],
    imports: [
        DokumentFormatIkonKomponent,
        MatIcon,
        MatIconButton,
        TranslateModule,
        MatMenuTrigger,
        DokumentMenyKomponent,
        MatTooltip,
        DokumentInlinePdfviserKomponent,
        FormaterPlainDatePipe,
        ZonedDateTimeSomPlainDatePipe,
        TittelVerdiKomponent,
        TittelKomponent,
        KlikkMedStopPropagationDirektiv
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DokumentListevisningKomponent extends DokumentvisningBasisKomponent {

    constructor(avhengigheter: DokumentvisningKomponentAvhengigheter,
                @Inject(DelegerteDokumentvisningOperasjoner_DI_TOKEN) delegerteOperasjoner: DelegerteDokumentvisningOperasjoner,
                dokumentInformasjon: DokumentInformasjonTjeneste,
                dokumentTjeneste: ÅpneDokumentTjeneste,
                dokumentSigneringTjeneste: DokumentSigneringTjeneste,
                oversettelser: Oversettelser,
                navigasjon: Navigasjon) {
        super(avhengigheter, delegerteOperasjoner, dokumentInformasjon, dokumentTjeneste, dokumentSigneringTjeneste, oversettelser, navigasjon);
    }

    //DokumentvisningBasisKomponent implementasjon

    protected override erVisInlineAnimasjonFerdig(hendelse: AnimationEvent): boolean {
        return hendelse.fromState === 'void';
    }
}

function skalerDokumentVedInlineVisning() {
    return trigger('skaler', [
        transition(':enter', [
              query('dokument-inline-pdfviser', [
                  style({height: '0', overflow: 'hidden'}),
                  animate('0.2s ease-in', style({height: '{{hoeydeEkspandert}}'}))
              ])
          ],
          skalerDokumentVedInlineVisningParametre()),
        transition(':leave', [
              query('dokument-inline-pdfviser', [
                  style({height: '{{hoeydeEkspandert}}', overflow: 'hidden'}),
                  animate('0.2s ease-in', style({height: '0'}))
              ])
          ],
          skalerDokumentVedInlineVisningParametre())
    ])
}

function skalerDokumentVedInlineVisningParametre() {
    return {params: {hoeydeEkspandert: '600px'}}
}
